/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

type CatalogItem = {
  id: number;
  name: string;
  type: "product" | "extra";
  price?: string;
  status: string;
  item: any;
};

interface ProductRowProps {
  item: CatalogItem;
  onToggleStatus: (
    id: number,
    type: "product" | "extra",
    enabled: boolean,
  ) => Promise<void>;
}

export const ProductRow = defineComponent<ProductRowProps>({
  name: "ProductRow",
  props: ["item", "onToggleStatus"],
  setup(props) {
    const isLoading = ref(false);

    const handleToggle = async (e: Event) => {
      isLoading.value = true;
      try {
        await props.onToggleStatus(
          props.item.id,
          props.item.type,
          (e.target as HTMLInputElement).checked,
        );
      } finally {
        isLoading.value = false;
      }
    };

    return () => (
      <tr class="">
        <td style="max-width: 50vh" class="font-medium">
          <div class="text-accent text-1xl flex items-center gap-2">
            {props.item.name}
          </div>
        </td>
        <td>
          {props.item.price
            ? formatPrice(props.item.price, false, 2, "CHF")
            : "-"}
        </td>
        <td class="text-center">
          <div class="flex justify-center items-center w-full h-full gap-2 h-6 w-6">
            {isLoading.value ? (
              <span class="loading loading-spinner loading-xs h-6"></span>
            ) : (
              <input
                type="checkbox"
                class="toggle toggle-success"
                checked={props.item.status === "available"}
                onChange={handleToggle}
              />
            )}
          </div>
        </td>
      </tr>
    );
  },
});

interface ProductsTableProps {
  items: CatalogItem[];
  onToggleStatus: (
    id: number,
    type: "product" | "extra",
    enabled: boolean,
  ) => Promise<void>;
}

export const ProductsTable = defineComponent<ProductsTableProps>({
  name: "ProductsTable",
  props: ["items", "onToggleStatus"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    return () => (
      <div class="overflow-x-auto h-full border  rounded-md">
        <table class="table table-pin-rows table-zebra rounded">
          <thead style="font-size: 16px" class="">
            <tr class="text-accent">
              <th style="max-width: 50vh">{t("products.core.item")}</th>
              <th>
                <span class="flex justify-start items-center">
                  {auth.user.value?.restaurant.currency || "CHF"}
                </span>
              </th>
              <th class="text-center text-right w-16">
                {t("products.core.status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {props.items.map((item) => (
              <ProductRow
                key={`${item.type}-${item.id}`}
                item={item}
                onToggleStatus={props.onToggleStatus}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  },
});
